import { useContext, useState } from 'react';
import t from 'react-translate';
import moment from 'moment';
import prodPathReplace from 'shared/prod-path-rewrite';
import { useFormContext } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { debounce } from 'lodash';

// Styles
import { css } from '@emotion/react';
import { almostBlack, blue05 } from 'athena/styles/colors';
import { gray2, primary } from 'styles/global_defaults/colors';
import { headerLineHeight } from 'styles/global_defaults/fonts';
import { doubleSpacing, quarterSpacing } from 'styles/global_defaults/scaffolding';

// Components
import NvDatePicker, { DatePickerType } from 'shared/components/inputs/nv-datepicker';
import NvIcon from 'shared/components/nv-icon';
import { SaveStatus } from 'redux/schemas/app/mentoring-programs';
import { getSettingsSaveStatus } from 'redux/selectors/mentoring-programs';
import NvTooltip from 'shared/components/nv-tooltip';

import { config } from '@config/pendo.config.json';
import { PROGRAM_CONFIGURATION_SECTION_WIDTH } from 'athena/components/mentoring-program/constants';
import MentoringProgramContext from '../../../context';

const INPUT_DATE_FORMAT = 'MM/DD/YYYY h:mm A';

const styles = css`
  max-width: ${PROGRAM_CONFIGURATION_SECTION_WIDTH}px;

  .description {
    color: ${almostBlack};
    line-height: ${headerLineHeight}px;
  }

  .match-announcement-date > div, .react-datepicker-wrapper {
    height: 55px;
    width: 100%;
  }

  .match-announcement-date {
    .clear-date-icon {
      position: relative;
      top: -${doubleSpacing}px;
      left: calc(100% - 25px);
    }
  }

  .match-announcement-icon {
    color: ${primary};
  }

  .note-container {
    background-color: ${blue05};
    border-radius: ${quarterSpacing}px;
  }

  .match-announcement-image {
    img {
      border-radius: 12px;
    }
  }
`;

type DatePickerSectionType = {
  name: string;
  description: string;
  placeholder: string;
  minDate: moment.Moment;
  maxDate: moment.Moment;
  disabled?: boolean;
  tooltip: string;
  onSubmit?: () => void;
};

const DatePickerSection = ({
  name,
  description,
  placeholder,
  minDate,
  maxDate,
  disabled,
  tooltip,
  onSubmit,
}: DatePickerSectionType) => {
  const { setValue, getValues, formState } = useFormContext();
  const { errors } = formState;
  const hasError = errors[name];
  const [dateValue, setDateValue] = useState(null);
  const saveStatus = useSelector(getSettingsSaveStatus);

  const debouncedSetDateValue = debounce((date) => {
    let dateTime = date;
    if (moment(date).isSame(moment(), 'day') && moment(date).isBefore(moment())) {
      dateTime = moment().add(1, 'hour');
      setValue(name, dateTime, { shouldValidate: true, shouldDirty: true });
    }
    setDateValue(dateTime);
  }, 300);
  const currentDateValue = getValues(name);

  const onDateSelection = () => {
    if (saveStatus !== SaveStatus.IN_PROGRESS) {
      if (getValues(name)) {
        onSubmit();
      }
    }
  };

  const clearValue = () => {
    setDateValue('');
    setValue(name, '');
    onSubmit();
  };

  const dataQATag = config.pendo.athena.mentorshipProgram.settings.matchingRules.matchingAnnouncementDateTimePicker;

  return (
    <div className='d-flex w-100 mb-2 col'>
      <div className='w-50 col'>
        <div className='d-flex w-100 description'>{description}</div>
      </div>
      <div className='d-flex w-50 align-items-center pl-4 match-announcement-date'>
        <NvTooltip text={tooltip} enabled={disabled}>
          <div>
            <NvDatePicker
              withForm
              min={minDate}
              max={maxDate}
              name={name}
              type={DatePickerType.DATETIME}
              placeholder={placeholder}
              onChange={debouncedSetDateValue}
              onCalendarClose={onDateSelection}
              disabled={disabled}
              dataQa={dataQATag}
              highlightDates={[moment().startOf('day')]}
              placement='left'
              validPeriodTime
            />
            {(dateValue || currentDateValue) && !hasError ? (
              <NvIcon
                icon='close'
                size='xss-smallest'
                className='cursor-pointer clear-date-icon inline gray-2'
                color={gray2}
                onClick={clearValue}
                altLabel={t.NOVOED.CLOSE()}
              />
            ) : null}
          </div>
        </NvTooltip>
      </div>
    </div>
  );
};

type NoteType = {
  translationBase: any;
};

const Note = ({ translationBase }: NoteType) => (
  <div className='d-flex w-100 note-container p-3 col align-items-center'>
    <NvIcon icon='ideas' size='small' className='mr-2 match-announcement-icon' />
    <div className='mr-2 gray-1 description'>
      {translationBase.NOTE_NO_DATE()}
    </div>
    <div className='match-announcement-image'>
      <img
        src={prodPathReplace('images/match-announcement-date.gif')}
        alt={translationBase.TITLE()}
      />
    </div>
  </div>
);

const MatchAnnouncementDate = ({ onSubmit, disabled }) => {
  const { mentoringProgram } = useContext(MentoringProgramContext);
  const { releasedDate, endDate } = mentoringProgram || {};
  const translationBase = t.MENTORING_PROGRAMS.SETTINGS.TABS.MATCHING_SETTINGS.MATCHING_TYPE.CONFIGURATION.ASSIGNED.MATCH_ANNOUNCEMENT_DATE;

  return (
    <div css={styles} className='mx-auto mb-5'>
      <div className='d-flex w-100 mb-2 heading-6 bold'>
        {translationBase.TITLE()}
      </div>
      <DatePickerSection
        name='matchAnnouncementDate'
        description={translationBase.DESCRIPTION()}
        placeholder={translationBase.PLACEHOLDER()}
        minDate={moment(releasedDate).isAfter(moment()) ? moment(releasedDate) : moment().add(1, 'hour')}
        maxDate={moment(endDate)}
        disabled={disabled}
        tooltip={translationBase.TOOLTIP()}
        onSubmit={onSubmit}
      />
      <Note translationBase={translationBase} />
    </div>
  );
};

export default MatchAnnouncementDate;
